import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';

const OVERLAY_CLOSE_FALLBACK_MS = 320;

function closeSizeOverlayElement(overlay) {
  if (!overlay) return;
  overlay._sizeOverlayCloseCleanup?.();
  overlay._sizeOverlayCloseCleanup = null;

  overlay.classList.add('quick-add-size-overlay--closing');
  overlay.classList.remove('quick-add-size-overlay--open');
  overlay.setAttribute('aria-hidden', 'true');

  let finished = false;
  const done = () => {
    if (finished) return;
    finished = true;
    overlay.removeEventListener('transitionend', onTransitionEnd);
    clearTimeout(fallbackTimer);
    overlay._sizeOverlayCloseCleanup = null;
    overlay.classList.remove('quick-add-size-overlay--closing');
    overlay.hidden = true;
  };

  const onTransitionEnd = (/** @type {TransitionEvent} */ event) => {
    if (event.target !== overlay) return;
    if (event.propertyName !== 'transform') return;
    done();
  };

  overlay.addEventListener('transitionend', onTransitionEnd);
  const fallbackTimer = window.setTimeout(done, OVERLAY_CLOSE_FALLBACK_MS);

  overlay._sizeOverlayCloseCleanup = () => {
    if (finished) return;
    overlay.removeEventListener('transitionend', onTransitionEnd);
    clearTimeout(fallbackTimer);
    overlay._sizeOverlayCloseCleanup = null;
  };
}

function closeAllSizeOverlaysExcept(exceptOverlay) {
  document.querySelectorAll('.quick-add-size-overlay').forEach((el) => {
    if (el !== exceptOverlay) closeSizeOverlayElement(el);
  });
  const owner =
    exceptOverlay instanceof Element ? exceptOverlay.closest('quick-add-component') : null;
  document.querySelectorAll('quick-add-component[stay-visible]').forEach((cmp) => {
    if (cmp !== owner) cmp.removeAttribute('stay-visible');
  });
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
    this.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdateCloseOverlay);
    this.addEventListener('click', this.#onSizeOptionClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
    this.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdateCloseOverlay);
    this.removeEventListener('click', this.#onSizeOptionClick);
    this.#abortController?.abort();
    this.#detachGlobalListeners();
    this.#closeSizeOverlay();
  }

  #onBreakpointChange = () => {
    this.#closeSizeOverlay();
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent instanceof QuickAddDialog) {
      dialogComponent.closeDialog();
    }
  };

  #onCartUpdateCloseOverlay = (event) => {
    if (event.detail?.data?.didError) return;
    const productForm = this.querySelector('product-form-component');
    if (!productForm || event.detail?.data?.productId !== productForm.dataset.productId) return;
    this.#closeSizeOverlay();
  };

  #onSizeOptionClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const btn = target.closest('.quick-add-size-overlay__option');
    if (!btn || !(btn instanceof HTMLButtonElement)) return;
    if (btn.disabled || btn.dataset.available === 'false') return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const productForm = this.querySelector('product-form-component');
    if (!productForm || typeof productForm.handleSubmit !== 'function') return;

    const variantInput = productForm.refs?.variantId;
    if (!(variantInput instanceof HTMLInputElement)) return;

    variantInput.value = variantId;
    variantInput.removeAttribute('disabled');

    productForm.handleSubmit(new Event('submit', { bubbles: true, cancelable: true }));
  };

  handleClick = (event) => {
    event.preventDefault();

    const overlay = this.refs.sizeOverlay;
    if (overlay instanceof HTMLElement) {
      if (overlay.classList.contains('quick-add-size-overlay--open')) {
        this.#closeSizeOverlay();
        return;
      }
      closeAllSizeOverlaysExcept(overlay);
      this.#openSizeOverlay();
    }
  };

  #openSizeOverlay() {
    const overlay = this.refs.sizeOverlay;
    if (!(overlay instanceof HTMLElement)) return;

    overlay._sizeOverlayCloseCleanup?.();
    overlay._sizeOverlayCloseCleanup = null;
    overlay.classList.remove('quick-add-size-overlay--closing');

    overlay.removeAttribute('hidden');
    overlay.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => {
      overlay.classList.add('quick-add-size-overlay--open');
    });
    this.toggleAttribute('stay-visible', true);

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    requestAnimationFrame(() => {
      document.addEventListener('pointerdown', this.#onDocumentPointerDown, { capture: true, signal });
    });
    document.addEventListener('keydown', this.#onDocumentKeyDown, { signal });
  }

  #closeSizeOverlay() {
    const overlay = this.refs.sizeOverlay;
    closeSizeOverlayElement(overlay);
    this.toggleAttribute('stay-visible', false);
    this.#detachGlobalListeners();
  }

  #detachGlobalListeners() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #onDocumentPointerDown = (event) => {
    const card = this.closest('product-card');
    const target = event.target;
    if (!card || !(target instanceof Node)) return;
    if (card.contains(target)) return;
    this.#closeSizeOverlay();
  };

  #onDocumentKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#closeSizeOverlay();
    }
  };
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
